import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';

import {
  ApplicationGlobalSettingResponse,
  ApplicationSetting,
  ApplicationTitle,
  CreateApplicationGlobalSettingRequest,
  UpdateApplicationGlobalSettingRequest,
} from '../../../data/models/application-setting.model';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  createApplicationGlobalSettingAsync,
  selectSavedApplicationGlobalSetting,
  selectSavingApplicationGlobalSetting,
  setSavedApplicationGlobalSetting,
  setSavingApplicationGlobalSetting,
  updateApplicationGlobalSettingAsync,
} from '../../../data/settings.slice';

import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';

const PREFIX = 'ApplicationGlobalSettingDialog';

const classes = {
  dialog: `${PREFIX}-dialog`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialog}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.actions}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    justifyContent: 'start',
    padding: theme.spacing(2),
  },
}));

interface ApplicationGlobalSettingDialogProps {
  open: boolean;
  globalSetting: ApplicationGlobalSettingResponse | null;
  isReadOnly: boolean;
  applicationTitles: ApplicationTitle[];
  applicationSettings: ApplicationSetting[];
  onClose: () => void;
}

export default function ApplicationGlobalSettingDialog({
  open,
  globalSetting,
  isReadOnly,
  applicationTitles,
  applicationSettings,
  onClose,
}: ApplicationGlobalSettingDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const title = globalSetting
    ? t('applicationGlobalSettings.editDialogTitle')
    : t('applicationGlobalSettings.addDialogTitle');

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onTouched',
  });
  const { isDirty, isValid } = formState;

  const savingApplicationGlobalSetting = useAppSelector(
    selectSavingApplicationGlobalSetting,
  );
  const savedApplicationGlobalSetting = useAppSelector(
    selectSavedApplicationGlobalSetting,
  );

  const saveApplicationGlobalSetting = (data: any) => {
    dispatch(setSavingApplicationGlobalSetting(false));
    dispatch(setSavedApplicationGlobalSetting(false));
    if (globalSetting) {
      const payload: UpdateApplicationGlobalSettingRequest = {
        applicationSettingID: data.applicationGlobalSettingID,
        applicationSettingValue: data.applicationSettingValue,
      };
      dispatch(updateApplicationGlobalSettingAsync(payload));
    } else {
      const payload: CreateApplicationGlobalSettingRequest = {
        applicationTitleID: data.applicationTitleID,
        applicationSettingID: data.applicationSettingID,
        applicationSettingValue: data.applicationSettingValue,
      };
      dispatch(createApplicationGlobalSettingAsync(payload));
    }
  };

  useEffect(() => {
    if (open) {
      const defaults: any = {
        ...globalSetting,
        applicationTitleID:
          globalSetting?.ApplicationTitle.applicationTitleID ?? '',
        applicationSettingID:
          globalSetting?.ApplicationSetting.applicationSettingID ?? '',
        applicationSettingValue: globalSetting?.applicationSettingValue ?? '',
      };
      reset(defaults);
    }
  }, [open, globalSetting, applicationTitles, applicationSettings]);

  useEffect(() => {
    if (savedApplicationGlobalSetting) {
      dispatch(setSavedApplicationGlobalSetting(false));
      dispatch(setSavingApplicationGlobalSetting(false));
      onClose();
    }
  }, [savedApplicationGlobalSetting, onClose]);

  return (
    <RootDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent>
        <Box className={classes.content}>
          <SelectMenu
            label={t('applicationGlobalSettings.applicationTitle')}
            name="applicationTitleID"
            values={applicationTitles}
            valueAttr="applicationTitleID"
            labelAttr="applicationName"
            required={true}
            control={control}
            disabled={isReadOnly}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('applicationGlobalSettings.applicationSetting')}
            name="applicationSettingID"
            values={applicationSettings}
            valueAttr="applicationSettingID"
            labelAttr="applicationSettingName"
            required={true}
            control={control}
            disabled={isReadOnly}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            label={t('applicationGlobalSettings.applicationSettingValue')}
            name="applicationSettingValue"
            required={true}
            control={control}
            disabled={isReadOnly}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 255 }}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !isValid || !isDirty || isReadOnly || savingApplicationGlobalSetting
          }
          onClick={handleSubmit(saveApplicationGlobalSetting)}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
